import { ref, nextTick, computed, type Ref, type ComputedRef } from 'vue'
import type { ProcessedMsg } from '@/types/chat'

export interface UseMessageSearchOptions {
  processedMsgs: ComputedRef<ProcessedMsg[]>
  msgsEl: Ref<HTMLElement | null>
  /** Assinatura do usuário logado (para separar o texto da mensagem da assinatura ao destacar). */
  messageSignature: () => string
  loadMoreMsgs: () => Promise<void>
  noMoreMsgs: Ref<boolean>
}

/** Busca textual dentro das mensagens já carregadas de uma conversa, carregando mais antigas conforme necessário. */
export function useMessageSearch(options: UseMessageSearchOptions) {
  const { processedMsgs, msgsEl, messageSignature, loadMoreMsgs, noMoreMsgs } = options

  const msgSearchOpen    = ref(false)
  const msgSearchQuery   = ref('')
  const msgSearchActive  = ref('')
  const msgSearchLoading = ref(false)
  const msgCurrentIdx    = ref(0)
  const msgSearchInputEl = ref<HTMLInputElement | null>(null)

  const msgMatchIds = computed<number[]>(() => {
    if (!msgSearchActive.value) return []
    const q = msgSearchActive.value.toLowerCase()
    return processedMsgs.value.filter(m => m.content && m.content.toLowerCase().includes(q)).map(m => m.id)
  })

  function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  // Chatwoot pode retornar message_signature com HTML. Strip para comparação e envio.
  function stripHtml(s: string): string {
    return s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim()
  }

  function getSig(): string {
    return stripHtml(messageSignature() || '')
  }

  function highlightMsgHtml(content: string | null, isOutgoing = false, sigRaw?: string): string {
    if (!content) return ''
    let main = content
    let sigHtml = ''
    if (isOutgoing) {
      const sig = stripHtml(sigRaw ?? messageSignature() ?? '')
      if (sig && content.length > sig.length) {
        let cut = -1
        if (content.endsWith(`\n\n${sig}`))    cut = content.length - sig.length - 2
        else if (content.endsWith(`\n${sig}`)) cut = content.length - sig.length - 1
        else if (content.endsWith(sig))        cut = content.length - sig.length
        if (cut >= 0) {
          main = content.slice(0, cut)
          sigHtml = `<span class="msg-sig">${escapeHtml(sig).replace(/\n/g, '<br>')}</span>`
        }
      }
    }
    if (!msgSearchActive.value) return escapeHtml(main).replace(/\n/g, '<br>') + sigHtml
    const q = msgSearchActive.value.toLowerCase()
    const result: string[] = []
    let i = 0
    while (i < main.length) {
      const j = main.toLowerCase().indexOf(q, i)
      if (j === -1) { result.push(escapeHtml(main.slice(i)).replace(/\n/g, '<br>')); break }
      if (j > i) result.push(escapeHtml(main.slice(i, j)).replace(/\n/g, '<br>'))
      result.push(`<mark class="msg-hl">${escapeHtml(main.slice(j, j + q.length))}</mark>`)
      i = j + q.length
    }
    return result.join('') + sigHtml
  }

  function openMsgSearch() {
    msgSearchOpen.value = true
    nextTick(() => msgSearchInputEl.value?.focus())
  }

  function closeMsgSearch() {
    msgSearchOpen.value = false
    msgSearchQuery.value = ''
    msgSearchActive.value = ''
    msgCurrentIdx.value = 0
  }

  async function doMsgSearch() {
    const q = msgSearchQuery.value.trim()
    if (!q) { msgSearchActive.value = ''; msgCurrentIdx.value = 0; return }
    msgSearchLoading.value = true
    msgSearchActive.value = q
    msgCurrentIdx.value = 0
    try {
      let iterations = 0
      while (!noMoreMsgs.value && iterations < 100) {
        await loadMoreMsgs()
        iterations++
      }
    } finally {
      msgSearchLoading.value = false
      await nextTick()
      scrollToMsgMatch()
    }
  }

  function goNextMsg() {
    if (!msgMatchIds.value.length) return
    msgCurrentIdx.value = (msgCurrentIdx.value + 1) % msgMatchIds.value.length
    nextTick(() => scrollToMsgMatch())
  }

  function goPrevMsg() {
    if (!msgMatchIds.value.length) return
    msgCurrentIdx.value = (msgCurrentIdx.value - 1 + msgMatchIds.value.length) % msgMatchIds.value.length
    nextTick(() => scrollToMsgMatch())
  }

  function scrollToMsgMatch() {
    const id = msgMatchIds.value[msgCurrentIdx.value]
    if (!id || !msgsEl.value) return
    const el = msgsEl.value.querySelector(`[data-msg-id="${id}"]`)
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  return {
    msgSearchOpen, msgSearchQuery, msgSearchActive, msgSearchLoading, msgCurrentIdx, msgSearchInputEl,
    msgMatchIds,
    escapeHtml, stripHtml, getSig, highlightMsgHtml,
    openMsgSearch, closeMsgSearch, doMsgSearch, goNextMsg, goPrevMsg, scrollToMsgMatch,
  }
}
